import { supabase } from '../lib/supabase'
import {
  applyFormAmountsToPaymentRows,
  buildPaymentRowsFromTemplates,
  buildPaymentTemplateRows,
  paymentTemplatesService,
  mapPaymentTemplate,
  paymentsWithTemplateAmounts,
  snapshotPaymentRows,
  snapshotTemplateRows,
} from './paymentTemplates'
import type {
  MarketingPayment,
  MarketingPaymentGroupInput,
} from '../types/marketingNotas'
import type {
  Contract,
  ContractAmount,
  ContractAmountInput,
  ContractAttachment,
  ContractInput,
  SoftDeleteParams,
} from '../types/contratos'

const BUCKET = 'contracts'

export const CONTRACT_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024
export const CONTRACT_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf,.pdf'

const CONTRACT_SELECT = `
  *,
  supplier:suppliers!contracts_supplier_id_fkey(id, name),
  payment_source:payment_sources(id, name, cnpj),
  category:contract_categories(id, name),
  department:departments!contracts_department_id_fkey(id, name, display_name),
  responsible:profiles!contracts_responsible_id_fkey(id, name),
  campaign:marketing_campaigns(id, name),
  event:marketing_events(id, name),
  request:marketing_requests(id, request_number),
  amounts:contract_amounts(
    *,
    department:departments!contract_amounts_department_id_fkey(id, name, display_name)
  ),
  attachments:contract_attachments(*),
  payment_templates(
    *,
    payment_method:payment_methods(id, name, settlement)
  ),
  payments(
    *,
    payment_method:payment_methods(id, name, settlement)
  )
`

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() || ''
  return trimmed || null
}

function roundMoney(value: number): number {
  return Math.round(value * 10000) / 10000
}

function mapContract(row: Contract): Contract {
  const amounts = (row.amounts || [])
    .filter((a) => !a.deleted_at)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))
    .map((a) => ({ ...a, amount: Number(a.amount) }))
  const attachments = (row.attachments || [])
    .filter((a) => !a.deleted_at)
    .sort((a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at))
  const paymentTemplates = (row.payment_templates || [])
    .filter((t) => !t.deleted_at)
    .sort((a, b) => {
      if (a.group_index !== b.group_index) return a.group_index - b.group_index
      return a.installment_number - b.installment_number
    })
    .map(mapPaymentTemplate)
  const payments = ((row.payments || []) as MarketingPayment[])
    .filter((p) => !p.deleted_at)
    .sort((a, b) => {
      if (a.group_index !== b.group_index) return a.group_index - b.group_index
      return a.installment_number - b.installment_number
    })
    .map((p): MarketingPayment => ({
      ...p,
      amount: p.amount == null ? null : Number(p.amount),
      group_index: Number(p.group_index),
      installment_number: Number(p.installment_number),
      recurrence_competence: p.recurrence_competence || null,
      payment_status: p.payment_status === 'paid' ? 'paid' : 'pending',
      paid_at: p.paid_at || null,
      paid_by: p.paid_by || null,
    }))
  return {
    ...row,
    status: row.status === 'confirmed' ? 'confirmed' : 'draft',
    contract_number: row.contract_number || null,
    contact_name: row.contact_name || null,
    contact_phone: row.contact_phone || null,
    contact_email: row.contact_email || null,
    category_id: row.category_id || null,
    department_id: row.department_id || null,
    responsible_id: row.responsible_id || null,
    campaign_id: row.campaign_id || null,
    event_id: row.event_id || null,
    request_id: row.request_id || null,
    confirmed_at: row.confirmed_at || null,
    confirmed_by: row.confirmed_by || null,
    variable_amount: Boolean(row.variable_amount),
    amounts,
    attachments,
    payment_templates: paymentTemplates,
    payments,
  }
}

function resolveVariableAmount(
  renewalType: ContractInput['renewal_type'] | null | undefined,
  variableAmount?: boolean
): boolean {
  return renewalType === 'recorrente' && Boolean(variableAmount)
}

function normalizeDate(value: string | null | undefined, label: string): string | null {
  const trimmed = value?.trim() || ''
  if (!trimmed) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    throw new Error(`Informe uma data válida para ${label}.`)
  }
  return trimmed
}

function assertRecurringRenewalDate(
  adhesionDate: string | null,
  renewalType: ContractInput['renewal_type']
): void {
  if (renewalType !== 'recorrente') return
  if (!adhesionDate) {
    throw new Error('Informe a data de adesão para contratos recorrentes.')
  }
}

function isAllowedAttachment(file: File): boolean {
  const mime = (file.type || '').toLowerCase()
  if (
    mime.startsWith('image/jpeg') ||
    mime.startsWith('image/png') ||
    mime.startsWith('image/gif') ||
    mime.startsWith('image/webp')
  ) {
    return true
  }
  if (mime === 'application/pdf') return true
  return /\.(jpe?g|png|gif|webp|pdf)$/i.test(file.name)
}

function contractAmountsTotal(amounts: ContractAmountInput[]): number {
  return roundMoney(amounts.reduce((acc, row) => acc + Number(row.amount || 0), 0))
}

function validateAmounts(amounts: ContractAmountInput[], variableAmount = false): void {
  if (!amounts?.length) {
    throw new Error('Informe ao menos um serviço do contrato.')
  }
  for (const [index, row] of amounts.entries()) {
    const amount = Number(row.amount)
    if (!Number.isFinite(amount) || amount < 0 || (!variableAmount && amount <= 0)) {
      throw new Error(
        variableAmount
          ? amounts.length === 1
            ? 'Informe um valor maior ou igual a zero.'
            : `Informe um valor maior ou igual a zero no serviço ${index + 1}.`
          : amounts.length === 1
            ? 'Informe um valor maior que zero.'
            : `Informe um valor maior que zero no serviço ${index + 1}.`
      )
    }
    const from = normalizeDate(row.effective_from, `início da vigência (serviço ${index + 1})`)
    if (!from) throw new Error(`Informe o início da vigência no serviço ${index + 1}.`)
    normalizeDate(row.effective_to, `fim da vigência (serviço ${index + 1})`)
  }
}

function validatePayments(
  expectedTotal: number,
  payments: MarketingPaymentGroupInput[],
  variableAmount = false
): void {
  if (!payments?.length) {
    throw new Error('Informe ao menos uma forma de pagamento.')
  }
  if (expectedTotal < 0) {
    throw new Error('O total do contrato não pode ser negativo.')
  }

  let paymentsTotal = 0
  let hasPendingAmount = false
  for (const [groupIndex, group] of payments.entries()) {
    if (!group.payment_method_id?.trim()) {
      throw new Error(`Selecione a forma de pagamento no grupo ${groupIndex + 1}.`)
    }
    if (!group.installments?.length) {
      throw new Error(`Informe ao menos uma parcela no grupo ${groupIndex + 1}.`)
    }
    for (const [installmentIndex, installment] of group.installments.entries()) {
      if (!installment.due_date?.trim()) {
        throw new Error(
          `Informe o vencimento da parcela ${installmentIndex + 1} do grupo ${groupIndex + 1}.`
        )
      }
      const amount =
        installment.amount == null ? NaN : Number(installment.amount)
      if (!Number.isFinite(amount) || amount <= 0) {
        if (!variableAmount) {
          throw new Error(
            `Valor inválido na parcela ${installmentIndex + 1} do grupo ${groupIndex + 1}.`
          )
        }
        hasPendingAmount = true
        continue
      }
      paymentsTotal += amount
    }
  }

  if (variableAmount && hasPendingAmount) return

  paymentsTotal = roundMoney(paymentsTotal)
  if (Math.abs(paymentsTotal - expectedTotal) > 0.009) {
    throw new Error(
      `A soma dos pagamentos (${paymentsTotal.toFixed(2)}) deve ser igual ao total do contrato (${expectedTotal.toFixed(2)}).`
    )
  }
}

function assertDraft(contract: Contract, action: string): void {
  if (contract.status !== 'draft') {
    throw new Error(`Somente rascunhos podem ser ${action}.`)
  }
}

function assertEditable(contract: Contract, action: string): void {
  if (contract.deleted_at) {
    throw new Error(`Contrato excluído não pode ser ${action}.`)
  }
  if (contract.status === 'draft') return
  if (contract.status === 'confirmed' && contract.is_active) return
  if (contract.status === 'confirmed' && !contract.is_active) {
    throw new Error(`Contrato inativo não pode ser ${action}.`)
  }
  throw new Error(`Somente rascunhos ou contratos confirmados ativos podem ser ${action}.`)
}

function paymentSlotKey(
  recurrenceCompetence: string | null | undefined,
  groupIndex: number,
  installmentNumber: number
): string {
  return `${recurrenceCompetence || ''}|${groupIndex}|${installmentNumber}`
}

function normalizeCompetence(value: string | null | undefined): string | null {
  return value || null
}

function sameCompetence(
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  return normalizeCompetence(left) === normalizeCompetence(right)
}

async function softDeletePendingPaymentsForCompetences(
  contractId: string,
  competences: Array<string | null | undefined>,
  payload: ReturnType<typeof softDeletePayload>
): Promise<void> {
  const unique = Array.from(new Set(competences.map((value) => normalizeCompetence(value))))
  for (const competence of unique) {
    let query = supabase
      .from('payments')
      .update(payload)
      .eq('contract_id', contractId)
      .eq('payment_status', 'pending')
      .is('deleted_at', null)

    query =
      competence == null
        ? query.is('recurrence_competence', null)
        : query.eq('recurrence_competence', competence)

    const { error } = await query
    if (error) throw error
  }
}

function throwContratosError(
  error: { message?: string; code?: string; details?: string; hint?: string } | null,
  fallback = 'Erro ao salvar o contrato.'
): never {
  const code = error?.code || ''
  const blob = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`
  if (
    code === '23505' ||
    /contracts_name_active_unique|duplicate key/i.test(blob)
  ) {
    throw new Error('Já existe um contrato ativo com este nome. Altere o nome para salvar.')
  }
  if (code === '23503' || /foreign key|violates foreign key/i.test(blob)) {
    throw new Error(
      'Há um vínculo inválido (fornecedor, categoria, solicitação, campanha ou evento). Verifique os dados.'
    )
  }
  const message = (error?.message || error?.details || '').trim()
  throw new Error(message || fallback)
}

function todayDateOnly(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Serviço vigente na data: início já passou (ou vazio) e fim ainda não passou (ou aberto). */
export function isContractAmountCurrent(
  amount: Pick<ContractAmount, 'effective_from' | 'effective_to'>,
  today = todayDateOnly()
): boolean {
  const from = amount.effective_from?.slice(0, 10) || ''
  const to = amount.effective_to?.slice(0, 10) || ''
  if (from && from > today) return false
  if (to && to < today) return false
  return true
}

export function getCurrentContractAmount(amounts: ContractAmount[] | undefined): ContractAmount | null {
  if (!amounts?.length) return null
  const current = amounts.find((a) => isContractAmountCurrent(a))
  if (current) return current
  return amounts[0] ?? null
}

/** Soma dos serviços vigentes na data de hoje. */
export function getOpenAmountsTotal(amounts: ContractAmount[] | undefined): number {
  if (!amounts?.length) return 0
  const open = amounts.filter((a) => isContractAmountCurrent(a))
  if (!open.length) return 0
  return contractAmountsTotal(
    open.map((a) => ({
      amount: a.amount,
      effective_from: a.effective_from,
      effective_to: a.effective_to,
      department_id: a.department_id,
      notes: a.notes,
    }))
  )
}

async function restoreContractFinancialState(
  contractId: string,
  templates: ReturnType<typeof snapshotTemplateRows>,
  payments: ReturnType<typeof snapshotPaymentRows>,
  actorId?: string | null
): Promise<void> {
  await paymentTemplatesService.restoreForOwner(
    { contract_id: contractId },
    templates,
    {
      actorId: actorId ?? null,
      deleteReason: 'rollback restore templates',
    }
  )

  const rollbackPayload = softDeletePayload({
    deleted_by: actorId ?? null,
    deleted_reason: 'rollback restore payments',
  })
  // Não toca parcelas baixadas nem outras competências; só reverte o escopo do snapshot.
  await softDeletePendingPaymentsForCompetences(
    contractId,
    payments.map((payment) => payment.recurrence_competence),
    rollbackPayload
  )

  if (!payments.length) return
  const { error: insertPaymentsError } = await supabase.from('payments').insert(payments)
  if (insertPaymentsError) throw insertPaymentsError
}

function snapshotOpenContractAmountRows(existing: Contract): Array<Record<string, unknown>> {
  return (existing.amounts || [])
    .filter((amount) => !amount.deleted_at && amount.effective_to == null)
    .map((amount) => ({
      contract_id: existing.id,
      department_id: amount.department_id,
      amount: amount.amount,
      effective_from: amount.effective_from,
      effective_to: amount.effective_to,
      notes: amount.notes,
      created_by: amount.created_by,
    }))
}

async function restoreContractUpdateState(
  existing: Contract,
  amountRows: Array<Record<string, unknown>>,
  templates: ReturnType<typeof snapshotTemplateRows>,
  payments: ReturnType<typeof snapshotPaymentRows>,
  actorId?: string | null
): Promise<void> {
  const { error: headerError } = await supabase
    .from('contracts')
    .update({
      name: existing.name,
      description: existing.description,
      contract_number: existing.contract_number,
      contact_name: existing.contact_name,
      contact_phone: existing.contact_phone,
      contact_email: existing.contact_email,
      supplier_id: existing.supplier_id,
      payment_source_id: existing.payment_source_id,
      category_id: existing.category_id,
      department_id: existing.department_id,
      responsible_id: existing.responsible_id,
      campaign_id: existing.campaign_id,
      event_id: existing.event_id,
      adhesion_date: existing.adhesion_date,
      renewal_type: existing.renewal_type,
      variable_amount: existing.variable_amount,
      termination_date: existing.termination_date,
      is_active: existing.is_active,
      request_id: existing.request_id,
    })
    .eq('id', existing.id)
    .is('deleted_at', null)
  if (headerError) throw headerError

  const rollbackPayload = softDeletePayload({
    deleted_by: actorId ?? null,
    deleted_reason: 'rollback restore amounts',
  })
  const { error: softAmountsError } = await supabase
    .from('contract_amounts')
    .update(rollbackPayload)
    .eq('contract_id', existing.id)
    .is('deleted_at', null)
    .is('effective_to', null)
  if (softAmountsError) throw softAmountsError

  if (amountRows.length) {
    const { error: insertAmountsError } = await supabase
      .from('contract_amounts')
      .insert(amountRows)
    if (insertAmountsError) throw insertAmountsError
  }

  await restoreContractFinancialState(existing.id, templates, payments, actorId)
}

export const contratosService = {
  async list(includeInactive = true): Promise<Contract[]> {
    let query = supabase
      .from('contracts')
      .select(CONTRACT_SELECT)
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return ((data || []) as Contract[]).map(mapContract)
  },

  async listByRequest(requestId: string): Promise<Contract[]> {
    if (!requestId) return []
    const { data, error } = await supabase
      .from('contracts')
      .select(CONTRACT_SELECT)
      .eq('request_id', requestId)
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (error) throw error
    return ((data || []) as Contract[]).map(mapContract)
  },

  async getById(id: string): Promise<Contract | null> {
    const { data, error } = await supabase
      .from('contracts')
      .select(CONTRACT_SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    return data ? mapContract(data as Contract) : null
  },

  async create(input: ContractInput, createdBy?: string | null): Promise<Contract> {
    const name = input.name.trim()
    if (!name) throw new Error('Título é obrigatório.')
    if (!input.supplier_id) throw new Error('Fornecedor é obrigatório.')
    if (!input.payment_source_id) throw new Error('Fonte pagadora é obrigatória.')
    if (!input.category_id) throw new Error('Categoria é obrigatória.')

    const variableAmount = resolveVariableAmount(input.renewal_type, input.variable_amount)
    validateAmounts(input.amounts, variableAmount)
    const expectedTotal = contractAmountsTotal(input.amounts)
    validatePayments(expectedTotal, input.payments, variableAmount)
    const adhesionDate = normalizeDate(input.adhesion_date, 'adesão')
    assertRecurringRenewalDate(adhesionDate, input.renewal_type || null)

    const { data: header, error } = await supabase
      .from('contracts')
      .insert({
        name,
        description: emptyToNull(input.description),
        contract_number: emptyToNull(input.contract_number),
        contact_name: emptyToNull(input.contact_name),
        contact_phone: emptyToNull(input.contact_phone),
        contact_email: emptyToNull(input.contact_email),
        supplier_id: input.supplier_id,
        payment_source_id: input.payment_source_id,
        category_id: input.category_id,
        department_id: emptyToNull(input.department_id),
        responsible_id: emptyToNull(input.responsible_id),
        campaign_id: emptyToNull(input.campaign_id),
        event_id: emptyToNull(input.event_id),
        adhesion_date: adhesionDate,
        renewal_type: input.renewal_type || null,
        variable_amount: variableAmount,
        termination_date: normalizeDate(input.termination_date, 'data de término'),
        is_active: input.is_active ?? true,
        request_id: emptyToNull(input.request_id),
        status: 'draft',
      })
      .select('id')
      .single()

    if (error) throwContratosError(error, 'Erro ao criar o contrato.')

    const amountRows = input.amounts.map((row) => ({
      contract_id: header.id,
      department_id: emptyToNull(row.department_id),
      amount: Number(row.amount),
      effective_from: normalizeDate(row.effective_from, 'início da vigência')!,
      effective_to: normalizeDate(row.effective_to, 'fim da vigência'),
      notes: emptyToNull(row.notes),
      created_by: row.created_by || createdBy || null,
    }))

    const { error: amountsError } = await supabase.from('contract_amounts').insert(amountRows)
    if (amountsError) {
      await supabase
        .from('contracts')
        .update(softDeletePayload({ deleted_reason: 'rollback create amounts' }))
        .eq('id', header.id)
      throwContratosError(amountsError, 'Erro ao salvar os serviços do contrato.')
    }

    try {
      const templateResult = await paymentTemplatesService.replaceForOwner(
        { contract_id: header.id },
        paymentsWithTemplateAmounts(input.payments, variableAmount),
        {
          recurring: input.renewal_type === 'recorrente',
          actorId: createdBy || null,
          deleteReason:
            input.renewal_type === 'recorrente'
              ? 'initial recurring template'
              : 'initial payment template',
        }
      )
      const paymentRows = applyFormAmountsToPaymentRows(
        buildPaymentRowsFromTemplates(
          templateResult.rows,
          templateResult.recurrenceCompetence
        ),
        input.payments,
        variableAmount
      )
      const { error: paymentsError } = await supabase.from('payments').insert(paymentRows)
      if (paymentsError) {
        throw paymentsError
      }
    } catch (paymentFlowError) {
      const rollbackPayload = softDeletePayload({
        deleted_by: createdBy || null,
        deleted_reason: 'rollback create templates/payments',
      })
      await supabase
        .from('payment_templates')
        .update(rollbackPayload)
        .eq('contract_id', header.id)
        .is('deleted_at', null)
      await supabase.from('payments').update(rollbackPayload).eq('contract_id', header.id).is('deleted_at', null)
      await supabase
        .from('contract_amounts')
        .update(rollbackPayload)
        .eq('contract_id', header.id)
        .is('deleted_at', null)
      await supabase.from('contracts').update(rollbackPayload).eq('id', header.id)
      throwContratosError(
        paymentFlowError &&
          typeof paymentFlowError === 'object' &&
          ('message' in paymentFlowError || 'code' in paymentFlowError)
          ? (paymentFlowError as {
              message?: string
              code?: string
              details?: string
              hint?: string
            })
          : null,
        'Erro ao salvar os pagamentos do contrato.'
      )
    }

    const created = await this.getById(header.id)
    if (!created) throw new Error('Contrato criado, mas não foi possível recarregar.')
    return created
  },

  async update(id: string, input: ContractInput, createdBy?: string | null): Promise<Contract> {
    const existing = await this.getById(id)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertEditable(existing, 'editados')

    const name = input.name.trim()
    if (!name) throw new Error('Título é obrigatório.')
    if (!input.supplier_id) throw new Error('Fornecedor é obrigatório.')
    if (!input.payment_source_id) throw new Error('Fonte pagadora é obrigatória.')
    if (!input.category_id) throw new Error('Categoria é obrigatória.')

    const variableAmount = resolveVariableAmount(input.renewal_type, input.variable_amount)
    validateAmounts(input.amounts, variableAmount)
    const expectedTotal = contractAmountsTotal(input.amounts)
    validatePayments(expectedTotal, input.payments, variableAmount)
    const adhesionDate = normalizeDate(input.adhesion_date, 'adesão')
    assertRecurringRenewalDate(adhesionDate, input.renewal_type || null)

    const { error } = await supabase
      .from('contracts')
      .update({
        name,
        description: emptyToNull(input.description),
        contract_number: emptyToNull(input.contract_number),
        contact_name: emptyToNull(input.contact_name),
        contact_phone: emptyToNull(input.contact_phone),
        contact_email: emptyToNull(input.contact_email),
        supplier_id: input.supplier_id,
        payment_source_id: input.payment_source_id,
        category_id: input.category_id,
        department_id: emptyToNull(input.department_id),
        responsible_id: emptyToNull(input.responsible_id),
        campaign_id: emptyToNull(input.campaign_id),
        event_id: emptyToNull(input.event_id),
        adhesion_date: adhesionDate,
        renewal_type: input.renewal_type || null,
        variable_amount: variableAmount,
        termination_date: normalizeDate(input.termination_date, 'data de término'),
        // Confirmado ativo/inativo só muda via cancelContract; o form não reativa/desativa.
        is_active:
          existing.status === 'confirmed' ? existing.is_active : input.is_active ?? true,
        request_id: emptyToNull(input.request_id),
      })
      .eq('id', id)
      .in('status', ['draft', 'confirmed'])
      .is('deleted_at', null)

    if (error) throwContratosError(error, 'Erro ao atualizar o contrato.')

    const replacePayload = softDeletePayload({ deleted_reason: 'replaced on update' })

    // Preserva histórico (com effective_to); só substitui serviços vigentes.
    const { error: softAmountsError } = await supabase
      .from('contract_amounts')
      .update(replacePayload)
      .eq('contract_id', id)
      .is('deleted_at', null)
      .is('effective_to', null)
    if (softAmountsError) throw softAmountsError

    const amountRows = input.amounts.map((row) => ({
      contract_id: id,
      department_id: emptyToNull(row.department_id),
      amount: Number(row.amount),
      effective_from: normalizeDate(row.effective_from, 'início da vigência')!,
      effective_to: normalizeDate(row.effective_to, 'fim da vigência'),
      notes: emptyToNull(row.notes),
      created_by: row.created_by || createdBy || null,
    }))

    const { error: amountsError } = await supabase.from('contract_amounts').insert(amountRows)
    if (amountsError) throw amountsError

    await this.replacePayments(
      id,
      input.payments,
      expectedTotal,
      input.renewal_type || null,
      createdBy || null,
      variableAmount
    )

    const updated = await this.getById(id)
    if (!updated) throw new Error('Contrato atualizado, mas não foi possível recarregar.')
    return updated
  },

  async replacePayments(
    contractId: string,
    payments: MarketingPaymentGroupInput[],
    expectedTotalOverride?: number,
    renewalType?: ContractInput['renewal_type'],
    actorId?: string | null,
    variableAmountOverride?: boolean
  ): Promise<void> {
    const existing = await this.getById(contractId)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertEditable(existing, 'editados')

    const expectedTotal =
      expectedTotalOverride != null
        ? roundMoney(expectedTotalOverride)
        : getOpenAmountsTotal(existing.amounts)
    const variableAmount =
      variableAmountOverride != null
        ? Boolean(variableAmountOverride)
        : resolveVariableAmount(renewalType ?? existing.renewal_type, existing.variable_amount)
    validatePayments(expectedTotal, payments, variableAmount)
    const previousAmountRows = snapshotOpenContractAmountRows(existing)
    const previousPaymentTemplates = snapshotTemplateRows(existing.payment_templates)
    const activePayments = (existing.payments || []).filter((payment) => !payment.deleted_at)
    const paidPayments = activePayments.filter((payment) => payment.payment_status === 'paid')
    const pendingPayments = activePayments.filter((payment) => payment.payment_status !== 'paid')
    const paidSlotKeys = new Set(
      paidPayments.map((payment) =>
        paymentSlotKey(
          payment.recurrence_competence,
          Number(payment.group_index),
          Number(payment.installment_number)
        )
      )
    )

    const recurring = renewalType === 'recorrente'
    const builtTemplates = buildPaymentTemplateRows(
      { contract_id: contractId },
      paymentsWithTemplateAmounts(payments, variableAmount),
      recurring
    )
    const paymentRows = applyFormAmountsToPaymentRows(
      buildPaymentRowsFromTemplates(builtTemplates.rows, builtTemplates.recurrenceCompetence),
      payments,
      variableAmount
    ).filter(
      (row) =>
        !paidSlotKeys.has(
          paymentSlotKey(row.recurrence_competence, row.group_index, row.installment_number)
        )
    )

    // Só substitui pendentes da(s) competência(s) do formulário; outras competências (n8n) ficam.
    const targetCompetences = Array.from(
      new Set(paymentRows.map((row) => normalizeCompetence(row.recurrence_competence)))
    )
    const pendingInScope = pendingPayments.filter((payment) =>
      targetCompetences.some((competence) =>
        sameCompetence(competence, payment.recurrence_competence)
      )
    )
    const previousPayments = snapshotPaymentRows(pendingInScope)

    try {
      if (targetCompetences.length) {
        await softDeletePendingPaymentsForCompetences(
          contractId,
          targetCompetences,
          softDeletePayload({ deleted_reason: 'replaced on update' })
        )
      }

      await paymentTemplatesService.replaceForOwner(
        { contract_id: contractId },
        paymentsWithTemplateAmounts(payments, variableAmount),
        {
          recurring,
          actorId: actorId ?? null,
          deleteReason: recurring ? 'replaced recurring template' : 'replaced payment template',
        }
      )

      if (paymentRows.length) {
        const { error: paymentsError } = await supabase.from('payments').insert(paymentRows)
        if (paymentsError) throw paymentsError
      }
    } catch (paymentFlowError) {
      await restoreContractUpdateState(
        existing,
        previousAmountRows,
        previousPaymentTemplates,
        previousPayments,
        actorId ?? null
      )
      throw paymentFlowError
    }
  },

  async cancelContract(
    id: string,
    options?: { termination_date?: string | null }
  ): Promise<Contract> {
    const existing = await this.getById(id)
    if (!existing) throw new Error('Contrato não encontrado.')
    if (existing.status !== 'confirmed') {
      throw new Error('Somente contratos confirmados podem ser cancelados.')
    }
    if (!existing.is_active) {
      throw new Error('Este contrato já está inativo.')
    }

    const terminationDate =
      normalizeDate(options?.termination_date, 'data de término') ||
      existing.termination_date ||
      new Date().toISOString().slice(0, 10)

    const { error } = await supabase
      .from('contracts')
      .update({
        is_active: false,
        termination_date: terminationDate,
      })
      .eq('id', id)
      .eq('status', 'confirmed')
      .eq('is_active', true)
      .is('deleted_at', null)

    if (error) throwContratosError(error, 'Erro ao cancelar o contrato.')

    const cancelled = await this.getById(id)
    if (!cancelled) throw new Error('Contrato cancelado, mas não foi possível recarregar.')
    return cancelled
  },

  async softDelete(id: string, params?: SoftDeleteParams): Promise<void> {
    const existing = await this.getById(id)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertDraft(existing, 'excluídos')

    const payload = softDeletePayload(params)

    const { error: amountsError } = await supabase
      .from('contract_amounts')
      .update(payload)
      .eq('contract_id', id)
      .is('deleted_at', null)
    if (amountsError) throw amountsError

    const { error: attachmentsError } = await supabase
      .from('contract_attachments')
      .update(payload)
      .eq('contract_id', id)
      .is('deleted_at', null)
    if (attachmentsError) throw attachmentsError

    const { error: paymentsError } = await supabase
      .from('payments')
      .update(payload)
      .eq('contract_id', id)
      .is('deleted_at', null)
    if (paymentsError) throw paymentsError

    const { error: templateError } = await supabase
      .from('payment_templates')
      .update(payload)
      .eq('contract_id', id)
      .is('deleted_at', null)
    if (templateError) throw templateError

    const { error } = await supabase
      .from('contracts')
      .update(payload)
      .eq('id', id)
      .eq('status', 'draft')
      .is('deleted_at', null)

    if (error) throw error
  },

  async confirmContract(id: string, confirmedBy?: string | null): Promise<Contract> {
    const existing = await this.getById(id)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertDraft(existing, 'confirmados')

    const { error } = await supabase
      .from('contracts')
      .update({
        status: 'confirmed',
        confirmed_at: new Date().toISOString(),
        confirmed_by: confirmedBy ?? null,
      })
      .eq('id', id)
      .eq('status', 'draft')
      .is('deleted_at', null)

    if (error) throwContratosError(error, 'Erro ao confirmar o contrato.')

    const confirmed = await this.getById(id)
    if (!confirmed) throw new Error('Contrato confirmado, mas não foi possível recarregar.')
    return confirmed
  },

  async listAmounts(contractId: string): Promise<ContractAmount[]> {
    const { data, error } = await supabase
      .from('contract_amounts')
      .select('*')
      .eq('contract_id', contractId)
      .is('deleted_at', null)
      .order('effective_from', { ascending: false })

    if (error) throw error
    return ((data || []) as ContractAmount[]).map((a) => ({ ...a, amount: Number(a.amount) }))
  },

  async createAmount(contractId: string, input: ContractAmountInput): Promise<ContractAmount> {
    const existing = await this.getById(contractId)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertEditable(existing, 'editados')

    if (input.amount == null || Number.isNaN(Number(input.amount)) || Number(input.amount) <= 0) {
      throw new Error('Informe um valor maior que zero.')
    }
    const effectiveFrom = normalizeDate(input.effective_from, 'início da vigência')
    if (!effectiveFrom) throw new Error('Informe a data de início da vigência.')
    const effectiveTo = normalizeDate(input.effective_to, 'fim da vigência')

    const { data, error } = await supabase
      .from('contract_amounts')
      .insert({
        contract_id: contractId,
        department_id: emptyToNull(input.department_id),
        amount: Number(input.amount),
        effective_from: effectiveFrom,
        effective_to: effectiveTo,
        notes: input.notes?.trim() || null,
        created_by: input.created_by || null,
      })
      .select('*')
      .single()

    if (error) throw error
    return { ...(data as ContractAmount), amount: Number((data as ContractAmount).amount) }
  },

  async updateAmount(id: string, input: Partial<ContractAmountInput>): Promise<ContractAmount> {
    const { data: amountRow, error: fetchError } = await supabase
      .from('contract_amounts')
      .select('id, contract_id')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (fetchError) throw fetchError
    if (!amountRow) throw new Error('Valor do contrato não encontrado.')

    const existing = await this.getById(amountRow.contract_id)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertEditable(existing, 'editados')

    const payload: Record<string, unknown> = {}
    if (input.amount !== undefined) {
      if (Number.isNaN(Number(input.amount)) || Number(input.amount) <= 0) {
        throw new Error('Informe um valor maior que zero.')
      }
      payload.amount = Number(input.amount)
    }
    if (input.effective_from !== undefined) {
      const effectiveFrom = normalizeDate(input.effective_from, 'início da vigência')
      if (!effectiveFrom) throw new Error('Informe a data de início da vigência.')
      payload.effective_from = effectiveFrom
    }
    if (input.effective_to !== undefined) {
      payload.effective_to = normalizeDate(input.effective_to, 'fim da vigência')
    }
    if (input.department_id !== undefined) {
      payload.department_id = emptyToNull(input.department_id)
    }
    if (input.notes !== undefined) payload.notes = input.notes?.trim() || null

    const { data, error } = await supabase
      .from('contract_amounts')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return { ...(data as ContractAmount), amount: Number((data as ContractAmount).amount) }
  },

  async softDeleteAmount(id: string, params?: SoftDeleteParams): Promise<void> {
    const { data: amountRow, error: fetchError } = await supabase
      .from('contract_amounts')
      .select('id, contract_id')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (fetchError) throw fetchError
    if (!amountRow) return

    const existing = await this.getById(amountRow.contract_id)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertEditable(existing, 'editados')

    const { error } = await supabase
      .from('contract_amounts')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async listAttachments(contractId: string): Promise<ContractAttachment[]> {
    const { data, error } = await supabase
      .from('contract_attachments')
      .select('*')
      .eq('contract_id', contractId)
      .is('deleted_at', null)
      .order('sort_order', { ascending: true })

    if (error) throw error
    return (data || []) as ContractAttachment[]
  },

  async uploadAttachment(
    file: File,
    contractId: string,
    uploadedBy?: string | null
  ): Promise<ContractAttachment> {
    const existing = await this.getById(contractId)
    if (!existing) throw new Error('Contrato não encontrado.')
    assertEditable(existing, 'editados')

    if (!isAllowedAttachment(file)) {
      throw new Error('Arquivo não permitido. Use imagem ou PDF.')
    }
    if (file.size > CONTRACT_ATTACHMENT_MAX_BYTES) {
      throw new Error('Arquivo excede o limite de 20 MB.')
    }

    const timestamp = Date.now()
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
    const filePath = `${contractId}/${timestamp}_${sanitizedFileName}`

    const { error: uploadError } = await supabase.storage.from(BUCKET).upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    })

    if (uploadError) {
      throw new Error(`Falha ao enviar anexo: ${uploadError.message}`)
    }

    const existingAtts = await this.listAttachments(contractId)
    const sortOrder = existingAtts.length

    const { data, error } = await supabase
      .from('contract_attachments')
      .insert({
        contract_id: contractId,
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || null,
        sort_order: sortOrder,
        uploaded_by: uploadedBy || null,
      })
      .select('*')
      .single()

    if (error) {
      await supabase.storage.from(BUCKET).remove([filePath])
      throw error
    }

    return data as ContractAttachment
  },

  async getAttachmentUrl(filePath: string, expiresInSeconds = 3600): Promise<string | null> {
    if (!filePath) return null
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(filePath, expiresInSeconds)
    if (error) {
      console.error('Erro ao gerar URL assinada do anexo:', error.message)
      return null
    }
    return data?.signedUrl ?? null
  },

  async softDeleteAttachment(id: string, params?: SoftDeleteParams): Promise<void> {
    const { data: att, error: fetchError } = await supabase
      .from('contract_attachments')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (fetchError) throw fetchError
    if (!att) return

    const contract = await this.getById(att.contract_id)
    if (contract) assertEditable(contract, 'editados')

    const { error } = await supabase
      .from('contract_attachments')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    try {
      await supabase.storage.from(BUCKET).remove([att.file_path])
    } catch {
      // Mantém soft delete mesmo se o storage falhar
    }
  },
}
